import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useAuth } from "@/contexts/AuthContext";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { CityAutocomplete } from "@/components/auth/CityAutocomplete";
import bandflowLogo from "@/assets/bandflow-logo.png";

const PROFILE_TYPES = [
  { value: "banda", label: "Banda" },
  { value: "artista", label: "Artista" },
  { value: "promotora", label: "Promotora" },
  { value: "manager", label: "Manager" },
  { value: "tecnico", label: "Técnico/a" },
  { value: "otro", label: "Otro" },
];

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);

  useSeoMeta({ pageType: isLogin ? "auth_login" : "auth_register" });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [visibleName, setVisibleName] = useState("");
  const [profileType, setProfileType] = useState("");
  const [city, setCity] = useState("");
  const [citySlug, setCitySlug] = useState<string | null>(null);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  // Determine redirect target
  const getRedirectPath = () => {
    const params = new URLSearchParams(location.search);
    return params.get("redirect") || (location.state as any)?.from || "/";
  };

  useEffect(() => {
    if (!authLoading && user) navigate(getRedirectPath(), { replace: true });
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const { data: signInData, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;

        const { data: profileData } = await supabase
          .from("profiles")
          .select("account_status")
          .eq("id", signInData.user.id)
          .single();

        if ((profileData as any)?.account_status === "blocked") {
          await supabase.auth.signOut();
          throw new Error("Tu cuenta ha sido bloqueada. Contacta con soporte para más información.");
        }

        toast({ title: "¡Bienvenido!" });
        navigate(getRedirectPath(), { replace: true });
      } else {
        if (!visibleName.trim()) throw new Error("El nombre visible es obligatorio");
        if (!profileType) throw new Error("Selecciona un tipo de perfil");
        if (!city.trim()) throw new Error("La ciudad es obligatoria");

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) throw error;

        if (data.user) {
          const { error: profileError } = await supabase
            .from("profiles")
            .update({
              visible_name: visibleName.trim(),
              profile_type: profileType,
              city: city.trim(),
              city_slug: citySlug,
              newsletter_opt_in: newsletterOptIn,
              newsletter_opt_in_at: newsletterOptIn ? new Date().toISOString() : null,
              newsletter_opt_in_source: newsletterOptIn ? "signup" : null,
            } as any)
            .eq("id", data.user.id);
          if (profileError) console.error("Error updating profile:", profileError);
        }

        toast({
          title: "Cuenta creada",
          description: "Revisa tu email para verificar tu cuenta.",
        });
        navigate(getRedirectPath(), { replace: true });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container flex items-center justify-center py-16">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img src={bandflowLogo} alt="Bandflow" className="h-12 w-auto" />
            </div>
            <CardTitle className="font-display text-2xl">
              {isLogin ? "Iniciar sesión" : "Crear cuenta"}
            </CardTitle>
            <CardDescription>
              {isLogin
                ? "Accede a tu cuenta para contribuir"
                : "Regístrate para compartir tus experiencias"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={loading}
              onClick={async () => {
                setLoading(true);
                try {
                  const { error } = await lovable.auth.signInWithOAuth("google", {
                    redirect_uri: window.location.origin,
                  });
                  if (error) throw error;
                } catch (err: any) {
                  toast({ title: "Error", description: err.message, variant: "destructive" });
                } finally {
                  setLoading(false);
                }
              }}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Continuar con Google
            </Button>

            <div className="my-4 flex items-center gap-3">
              <Separator className="flex-1" />
              <span className="text-xs text-muted-foreground">o</span>
              <Separator className="flex-1" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="tu@email.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="Mínimo 6 caracteres" />
              </div>

              {!isLogin && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="visibleName">Nombre visible</Label>
                    <Input id="visibleName" value={visibleName} onChange={(e) => setVisibleName(e.target.value)} required={!isLogin} placeholder="Ej: Los Planetas, María García" />
                    <p className="text-xs text-muted-foreground">Este nombre aparecerá públicamente en tus reviews</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profileType">Tipo de perfil</Label>
                    <Select value={profileType} onValueChange={setProfileType} required={!isLogin}>
                      <SelectTrigger><SelectValue placeholder="Selecciona tu perfil" /></SelectTrigger>
                      <SelectContent>
                        {PROFILE_TYPES.map((pt) => (
                          <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ciudad</Label>
                    <CityAutocomplete
                      value={city}
                      onSelect={(name, slug) => {
                        setCity(name);
                        setCitySlug(slug);
                      }}
                      required={!isLogin}
                    />
                  </div>

                  <div className="flex items-start space-x-3 pt-2">
                    <Checkbox
                      id="newsletter"
                      checked={newsletterOptIn}
                      onCheckedChange={(checked) => setNewsletterOptIn(checked === true)}
                    />
                    <Label htmlFor="newsletter" className="text-sm font-normal leading-snug cursor-pointer">
                      Acepto recibir un correo semanal con noticias y recursos relacionadas con la industria musical (¡Pocos correos y mucho valor!)
                    </Label>
                  </div>
                </>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Cargando..." : isLogin ? "Entrar" : "Crear cuenta"}
              </Button>
            </form>

            <div className="mt-4 text-center text-sm">
              <button type="button" onClick={() => setIsLogin(!isLogin)} className="text-primary hover:underline">
                {isLogin ? "¿No tienes cuenta? Regístrate" : "¿Ya tienes cuenta? Inicia sesión"}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
